import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";

type OptionType = {
  value: string;
  label: string;
  disabled?: boolean;
};

type TypesContextValue = {
  sexOptions: OptionType[];
  genderOptions: OptionType[];
  pronounsOptions: OptionType[];
  typeOfVisitOptions: OptionType[];
  modeOfArrivalOptions: OptionType[];
  streamOptions: OptionType[];
  injuryIntentOptions: OptionType[];
  injuryCauseOptions: OptionType[];
  triageDirectionOptions: OptionType[];
  patientOriginOptions: OptionType[];
  delegateRelationshipOptions: OptionType[];
  allergySeverityOptions: OptionType[];
};

// Static definition of allergy severity options
// TODO: Move to settings in the future
const ALLERGY_SEVERITY_OPTIONS: OptionType[] = [
  { value: "anaphylaxis", label: "Anaphylaxis" },
  { value: "breathing", label: "Breathing Issues" },
  { value: "swelling", label: "Swelling" },
  { value: "vomiting", label: "Vomiting" },
  { value: "rash", label: "Rash" },
  { value: "other", label: "Other" },
];

const TypesContext = createContext<TypesContextValue | null>(null);

export const useTypes = () => {
  const context = useContext(TypesContext);
  if (!context) {
    throw new Error("useTypes must be used within a TypesProvider");
  }
  return context;
};

export const TypesProvider = ({ children }: PropsWithChildren) => {
  const { settings } = useSettingsContext();

  const value = useMemo(() => {
    const patientDetails = (settings as any)?.patientDetails;
    const visitDetails = (settings as any)?.visitDetails;

    // Helper function to transform options from settings format to OptionType
    const transformOptions = (options: any[]): OptionType[] =>
      (options || []).map((option: any) => ({
        value: option.code,
        label: option.description,
        disabled: option.active === false,
      }));

    return {
      sexOptions: transformOptions(patientDetails?.sexOptions),
      genderOptions: transformOptions(patientDetails?.genderOptions),
      pronounsOptions: transformOptions(patientDetails?.pronounsOptions),
      typeOfVisitOptions: transformOptions(visitDetails?.typeOfVisitOptions),
      modeOfArrivalOptions: transformOptions(visitDetails?.modeOfArrivalOptions),
      streamOptions: transformOptions(visitDetails?.streamOptions),
      injuryIntentOptions: transformOptions(visitDetails?.injuryIntentOptions),
      injuryCauseOptions: transformOptions(visitDetails?.injuryCauseOptions),
      triageDirectionOptions: transformOptions(visitDetails?.triageDirectionOptions),
      patientOriginOptions: transformOptions(visitDetails?.patientOriginOptions),
      delegateRelationshipOptions: transformOptions(patientDetails?.delegateRelationshipOptions),
      allergySeverityOptions: ALLERGY_SEVERITY_OPTIONS,
    };
  }, [settings]);

  return (
    <TypesContext.Provider value={value}>
      {children}
    </TypesContext.Provider>
  );
};

